import { useEffect, useState } from "react";
import { IconButton, Menu, MenuItem } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { cls } from "../hatim/soft/SoftKit";
import { useToast } from "../../components/ui/Toast";
import {
  useListUsersQuery,
  useSetUserActiveMutation,
  useUnlockUserMutation,
  useSendUserResetMutation,
} from "../../api/adminApi";
import { formatDate, formatNumber, roleLabel, ROLE_LABELS, ADMIN_ROLE } from "../../lib/adminFormat";
import {
  PageHeader,
  Section,
  NoData,
  ErrorRetry,
  SkeletonRows,
  Pager,
  ConfirmDialog,
  Tag,
  SearchInput,
  SelectInput,
  useDebouncedValue,
} from "./components/AdminKit";

const ROLE_OPTIONS = [{ value: "", label: "Tüm roller" }].concat(
  Object.entries(ROLE_LABELS).map(([value, label]) => ({ value, label }))
);
const STATUS_OPTIONS = [
  { value: "", label: "Tüm durumlar" },
  { value: "active", label: "Etkin" },
  { value: "deleted", label: "Kapalı" },
];

function fullName(u) {
  return `${u.name || ""} ${u.surname || ""}`.trim() || u.email;
}

function StatusTags({ u }) {
  return (
    <span className="inline-flex flex-wrap gap-1">
      {u.isDeleted ? <Tag tone="danger">Kapalı</Tag> : <Tag tone="ok">Etkin</Tag>}
      {u.locked && (
        <Tag tone="warn">
          <LockOutlinedIcon sx={{ fontSize: 12, mr: 0.25 }} aria-hidden /> Kilitli
        </Tag>
      )}
    </span>
  );
}

function RowMenu({ u, onAction }) {
  const [anchor, setAnchor] = useState(null);
  if (u.role === ADMIN_ROLE) return <span className="w-10 inline-block" aria-hidden />;
  const pick = (action) => {
    setAnchor(null);
    onAction(action, u);
  };
  return (
    <>
      <IconButton size="small" aria-label={`${fullName(u)} için işlemler`} onClick={(e) => setAnchor(e.currentTarget)}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu open={Boolean(anchor)} anchorEl={anchor} onClose={() => setAnchor(null)}>
        {u.isDeleted ? (
          <MenuItem onClick={() => pick("activate")}>Hesabı aç</MenuItem>
        ) : (
          <MenuItem onClick={() => pick("deactivate")}>Hesabı kapat</MenuItem>
        )}
        {u.locked && <MenuItem onClick={() => pick("unlock")}>Giriş kilidini kaldır</MenuItem>}
        {!u.isDeleted && <MenuItem onClick={() => pick("reset")}>Şifre sıfırlama e-postası gönder</MenuItem>}
      </Menu>
    </>
  );
}

const CONFIRM = {
  deactivate: (u) => ({
    title: "Hesap kapatılsın mı?",
    text: `${fullName(u)} artık giriş yapamaz, açık oturumları da kapanır.`,
    label: "Hesabı kapat",
    danger: true,
  }),
  activate: (u) => ({ title: "Hesap açılsın mı?", text: `${fullName(u)} yeniden giriş yapabilir.`, label: "Hesabı aç" }),
  unlock: (u) => ({
    title: "Giriş kilidi kaldırılsın mı?",
    text: `${u.email} için hatalı deneme sayacı sıfırlanır.`,
    label: "Kilidi kaldır",
  }),
  reset: (u) => ({
    title: "Şifre sıfırlama e-postası gönderilsin mi?",
    text: `${u.email} adresine şifre sıfırlama bağlantısı gider.`,
    label: "Gönder",
  }),
};

export default function AdminUsersPage() {
  const toast = useToast();
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const debouncedQ = useDebouncedValue(q.trim());
  useEffect(() => setPage(1), [debouncedQ, role, status]);

  const { data, isLoading, isFetching, isError, error, refetch } = useListUsersQuery({
    q: debouncedQ,
    role,
    status,
    page,
  });
  const users = data?.users || [];

  const [setActive, setActiveState] = useSetUserActiveMutation();
  const [unlock, unlockState] = useUnlockUserMutation();
  const [sendReset, resetState] = useSendUserResetMutation();
  const busy = setActiveState.isLoading || unlockState.isLoading || resetState.isLoading;
  const [pending, setPending] = useState(null); // { action, user }

  const run = async () => {
    if (!pending) return;
    const { action, user } = pending;
    let result;
    let okText;
    if (action === "deactivate" || action === "activate") {
      result = await setActive({ userId: user.id, active: action === "activate" });
      okText = action === "activate" ? "Hesap açıldı." : "Hesap kapatıldı.";
    } else if (action === "unlock") {
      result = await unlock({ email: user.email });
      okText = "Giriş kilidi kaldırıldı.";
    } else if (action === "reset") {
      result = await sendReset({ userId: user.id });
      okText = "Şifre sıfırlama e-postası gönderildi.";
    }
    setPending(null);
    if (result?.error) toast.error(result.error.message || "İşlem yapılamadı.");
    else toast.success(okText);
  };

  const confirm = pending ? CONFIRM[pending.action](pending.user) : null;

  let body;
  if (isLoading) body = <SkeletonRows count={8} />;
  else if (isError) body = <ErrorRetry message={error?.message} onRetry={refetch} error={error} />;
  else if (users.length === 0) body = <NoData text="Süzgeçlere uyan kullanıcı yok" />;
  else {
    const th = `px-4 py-2.5 text-left text-sm font-Default ${cls.muted}`;
    const td = "px-4 py-3 align-middle";
    body = (
      <div className={`transition-opacity ${isFetching ? "opacity-60" : ""}`}>
        {/* Geniş ekran: tablo */}
        <table className="hidden md:table w-full">
          <thead>
            <tr className={cls.row}>
              <th className={th}>Ad soyad</th>
              <th className={th}>Rol</th>
              <th className={`${th} text-right`}>Grup</th>
              <th className={th}>Son giriş günü</th>
              <th className={th}>Kayıt</th>
              <th className={th}>Durum</th>
              <th className="w-12" />
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className={`${cls.row} ${cls.rowHover}`}>
                <td className={td}>
                  <div className={`text-base ${cls.title}`}>{fullName(u)}</div>
                  <div className={`text-sm ${cls.muted}`}>{u.email}</div>
                </td>
                <td className={`${td} text-base ${cls.muted}`}>{roleLabel(u.role)}</td>
                <td className={`${td} text-base text-right tabular-nums ${cls.title}`}>
                  {formatNumber(u.groupCount)}
                  {u.managedGroupCount > 0 && (
                    <span className={`block text-xs ${cls.faint}`}>{formatNumber(u.managedGroupCount)} yönetiyor</span>
                  )}
                </td>
                <td className={`${td} text-base ${cls.muted}`}>{u.lastEntryDate ? formatDate(u.lastEntryDate) : "Yok"}</td>
                <td className={`${td} text-base ${cls.muted}`}>{formatDate(u.createdAt)}</td>
                <td className={td}>
                  <StatusTags u={u} />
                </td>
                <td className={`${td} text-right`}>
                  <RowMenu u={u} onAction={(action, user) => setPending({ action, user })} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Dar ekran: çizgili satırlar */}
        <ul className="md:hidden">
          {users.map((u) => (
            <li key={u.id} className={`px-4 py-3 flex items-start gap-2 ${cls.row}`}>
              <div className="min-w-0 flex-1">
                <div className={`text-base truncate ${cls.title}`}>{fullName(u)}</div>
                <div className={`text-sm truncate ${cls.muted}`}>{u.email}</div>
                <div className={`text-xs mt-1 ${cls.faint}`}>
                  {roleLabel(u.role)} · {formatNumber(u.groupCount)} grup · son giriş{" "}
                  {u.lastEntryDate ? formatDate(u.lastEntryDate) : "yok"} · kayıt {formatDate(u.createdAt)}
                </div>
                <div className="mt-1.5">
                  <StatusTags u={u} />
                </div>
              </div>
              <RowMenu u={u} onAction={(action, user) => setPending({ action, user })} />
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto flex flex-col gap-5 pb-8">
      <PageHeader
        title="Kullanıcılar"
        subtitle={data?.total !== undefined ? `${formatNumber(data.total)} kullanıcı` : "Hesaplar ve giriş durumu"}
      />
      <Section bodyClassName="p-0">
        <div className={`px-4 py-3 flex flex-wrap gap-2 ${cls.row}`}>
          <SearchInput value={q} onChange={setQ} placeholder="Ad, soyad ya da e-posta ara" className="flex-1 min-w-[200px]" />
          <SelectInput value={role} onChange={setRole} options={ROLE_OPTIONS} label="Rol" />
          <SelectInput value={status} onChange={setStatus} options={STATUS_OPTIONS} label="Durum" />
        </div>
        {body}
        <Pager page={page} hasMore={Boolean(data?.hasMore)} onChange={setPage} busy={isFetching} />
      </Section>
      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirm?.title}
        text={confirm?.text}
        confirmLabel={confirm?.label}
        danger={confirm?.danger}
        busy={busy}
        onConfirm={run}
        onClose={() => setPending(null)}
      />
    </div>
  );
}
